import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
  Menu,
  Slider,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Divider,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CommonDownload from "../../custom-components/CommonDownload";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ReactComponent as Logo } from "../../../images/QMentis1.svg";
import {
  arrayOfObjectTransformer,
  objectDataTransformer,
} from "../../../utils/tableDataGenerator";
import wordGenerator from "../../download/qeBotWordGenerator";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";

const MultiBusinessProcessGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessProcessData } = location.state || [];
  const [openExportPage, setOpenExportPage] = useState(false);

  const [textSize, setTextSize] = useState(14);
  const [anchorEl, setAnchorEl] = useState(null);
  const [spaceIncreased, setSpaceIncreased] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [processedData, setProcessedData] = useState({
    commonDefectsTableData: null,
    appendixTableData: null,
  });

  const [zoomLevels, setZoomLevels] = useState({});

  useEffect(() => {
    if (businessProcessData && businessProcessData.parsedBusinessProcess) {
      setProcessedData({
        commonDefectsTableData: arrayOfObjectTransformer(
          businessProcessData.parsedBusinessProcess["List of Common Defects"]
        ),
        appendixTableData: objectDataTransformer(
          businessProcessData.parsedBusinessProcess["Appendix"]
        ),
      });
    }
  }, [businessProcessData]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTextSizeChange = (event, newValue) => {
    setTextSize(newValue);
  };

  const handleDownloadClick = () => {
    setDownloadOpen(true);
  };

  const handleCloseDownload = () => {
    setDownloadOpen(false);
  };

  const handleShare = () => {
    setOpenExportPage(true);
  };
  const handleSpaceToggle = () => {
    setSpaceIncreased((prev) => !prev);
  };
  const handleZoomIn = (idx) => {
    setZoomLevels((prevZoomLevels) => ({
      ...prevZoomLevels,
      [idx]: (prevZoomLevels[idx] || 1) + 0.1,
    }));
  };

  const handleZoomOut = (idx) => {
    setZoomLevels((prevZoomLevels) => ({
      ...prevZoomLevels,
      [idx]: (prevZoomLevels[idx] || 1) - 0.1,
    }));
  };

  const handleDownloadImage = (flowchartImage, idx) => {
    if (flowchartImage) {
      const link = document.createElement("a");
      link.href = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        flowchartImage
      )}`;
      link.download = `flowchart-${idx + 1}.svg`;
      link.click();
    }
  };

  const businessProcessGuideCall = async () => {
    try {
      const allSectionsData = await Promise.all(
        businessProcessData.map(async (useCase, idx) => {
          if (!useCase || !useCase.parsedBuinessProcess) {
            return null;
          }
          const sections = [
            {
              type: "text",
              heading: `Business Process Guide - ${idx + 1}`,
              data: "",
            },
            {
              type: "text",
              data:
                useCase.parsedBuinessProcess[
                  "Description of the Business Process"
                ] || "No description available.",
              heading: "Description",
            },
            {
              type: "image",
              data: useCase.flowchartResponse,
              heading: "Flow Chart",
            },
            {
              type: "table",
              data: arrayOfObjectTransformer(
                useCase.parsedBuinessProcess["List of Common Defects"] || []
              ),
              heading: "Common Defects",
            },
            {
              type: "table",
              data: objectDataTransformer(
                useCase.parsedBuinessProcess["Appendix"] || {}
              ),
              heading: "Appendix",
            },
          ];

          return sections;
        })
      );
      const flattenedAllSectionsData = allSectionsData.flat().filter(Boolean);

      await wordGenerator(flattenedAllSectionsData, "Business Process Guide");
    } catch (error) {
      console.error("Error generating documents:", error);
    }
  };

  const renderTable = (tableData) => {
    if (!tableData || !tableData.values || !tableData.values.length)
      return null;

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: textSize }}>
          {tableData.heading}
        </Typography>
        <Table
          sx={{
            borderCollapse: "collapse",
            width: "100%",
            mt: 2,
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              {tableData.headers.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    border: "1px solid #ccc",
                    bgcolor: "#49A3FF",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    padding: "12px",
                    textAlign: "left",
                    fontSize: `${textSize}px`,
                    width: index === 0 ? "25%" : "75%",
                    overflowWrap: "break-word",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.values.map((row, rowIndex) => (
              <TableRow key={rowIndex} sx={{ bgcolor: "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={{
                      border: "1px solid #ccc",
                      padding: "12px",
                      textAlign: "left",
                      fontSize: `${textSize}px`,
                      width: cellIndex === 0 ? "30%" : "70%", // Match header width
                      overflowWrap: "break-word",
                    }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const textStyle = {
    fontSize: textSize,
    lineHeight: spaceIncreased ? "1.8" : "1.4",
    paddingBottom: spaceIncreased ? "10px" : "5px",
  };

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "#f6f3f2",
        minHeight: "80vh",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        overflowY: "scroll",
        width: "100%",
        height: "70vh",
        right: -24,
        position: "relative",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
          minHeight: "20px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "1024px", margin: "0 auto", p: 2 }}>
        {/* AppBar */}
        <AppBar
          position="static"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            width: "100%",
            background: "#FFFFFF",
            boxShadow: "none",
            height: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          <Toolbar
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              padding: 0,
            }}
          >
            <IconButton
              onClick={handleBackClick}
              edge="start"
              aria-label="back"
              sx={{ color: "black" }}
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Right Corner Icons */}
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Tooltip title="Change Text Size">
                <IconButton onClick={handleMenuClick} sx={{ color: "black" }}>
                  <TextFormatIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Download">
                <IconButton
                  onClick={businessProcessGuideCall}
                  sx={{ color: "black" }}
                >
                  <ArrowCircleDownIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Share">
                <IconButton onClick={handleShare} sx={{ color: "black" }}>
                  <ShareOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Text Size Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              sx={{ "& .MuiPaper-root": { width: "277px", height: "196px" } }}
            >
              <Box padding={2}>
                <Typography variant="subtitle1" sx={{ color: "black" }}>
                  Text Size
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ color: "black" }}>
                    AA
                  </Typography>
                  <Slider
                    value={textSize}
                    onChange={handleTextSizeChange}
                    step={1}
                    marks={[
                      { value: 10, label: "" },
                      { value: 12, label: "" },
                      { value: 14, label: "" },
                      { value: 16, label: "" },
                      { value: 18, label: "" },
                      { value: 20, label: "" },
                    ]}
                    min={10}
                    max={20}
                    valueLabelDisplay="auto"
                    sx={{
                      marginLeft: 2,
                      marginRight: 2,
                      width: "110.5px",
                      height: "16px",
                      "& .MuiSlider-thumb": {
                        width: "4px",
                        height: "44px",
                        borderRadius: "2px",
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: "#49A3FF",
                        borderRadius: "16px",
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: "#E0E0E0",
                        borderRadius: "16px",
                      },
                      "& .MuiSlider-mark": {
                        height: "4px",
                        width: "4px",
                        borderRadius: "50%",
                        backgroundColor: "#49A3FF",
                      },
                      "& .MuiSlider-markActive": { backgroundColor: "white" },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "black" }}>
                    AA
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" marginTop={2}>
                  <Typography
                    variant="body1"
                    sx={{ color: "black", marginRight: 1 }}
                  >
                    Increase Spacing
                  </Typography>
                  <Switch
                    checked={spaceIncreased}
                    onChange={handleSpaceToggle}
                  />
                </Box>
              </Box>
            </Menu>
          </Toolbar>
          <Divider sx={{ borderColor: "#ccc", width: "100%" }} />
        </AppBar>

        {/* Business Process Guide Content */}
        <Box sx={{ p: 4, backgroundColor: "#FFFFFF" }}>
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              pb: 2,
              // borderBottom: "1px solid #e0e0e0"
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // mr: 2,
                  flexShrink: 0,
                }}
              >
                <Logo />
              </Box>
            </Box>
          </Box>

          {/* Sections for each Use Case */}

          {businessProcessData ? (
            businessProcessData.map((useCase, idx) =>
              useCase && useCase.parsedBuinessProcess ? (
                <Box key={idx}>
                  <Section
                    title={`Business Process Guide - Use Case ${idx + 1}`}
                  />

                  {/* Description Section */}
                  {/* <Section title="Description"> */}
                  <Typography variant="body1" sx={textStyle} paragraph>
                    {useCase.parsedBuinessProcess[
                      "Description of the Business Process"
                    ] || "No description available."}
                  </Typography>
                  {/* </Section> */}

                  {/* Flowchart Section */}
                  <Section title="Flow Chart" align="center">
                    {useCase.flowchartResponse && (
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <img
                            src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                              useCase.flowchartResponse
                            )}`}
                            alt={`Flowchart for Use Case ${idx + 1}`}
                            loading="lazy"
                            style={{
                              maxWidth: "100%",
                              height: "auto",
                              transform: `scale(${zoomLevels[idx] || 1})`,
                              border: "1px solid #ccc",
                              borderRadius: "10px",
                              padding: "5px",
                            }}
                          />
                          <OverlayActions
                            onDownload={() =>
                              handleDownloadImage(
                                useCase.flowchartResponse,
                                idx
                              )
                            }
                            onZoomIn={() => handleZoomIn(idx)}
                            onZoomOut={() => handleZoomOut(idx)}
                          />
                        </Box>
                      </Box>
                    )}
                  </Section>

                  {/* Common Defects Table */}
                  {useCase.parsedBuinessProcess["List of Common Defects"] && (
                    <Section title="Common Defects">
                      {renderTable({
                        // heading: "Common Defects",
                        headers: ["Defect", "Occurrence"],
                        values: useCase.parsedBuinessProcess[
                          "List of Common Defects"
                        ].map((defect) => [defect.Defect, defect.Occurrence]),
                      })}
                    </Section>
                  )}

                  {/* Appendix Table */}
                  {useCase.parsedBuinessProcess.Appendix && (
                    <Section title="Glossary">
                      {renderTable({
                        // heading: "Appendix",
                        headers: ["Term", "Definition"],
                        values: Object.entries(
                          useCase.parsedBuinessProcess.Appendix
                        ).map(([term, definition]) => [term, definition]),
                      })}
                    </Section>
                  )}
                </Box>
              ) : null
            )
          ) : (
            <Typography variant="body1">
              No business process data available.
            </Typography>
          )}
        </Box>
      </Box>
      {openExportPage && (
        <ALMConnectionPage
          issueType={"Business Process Guide"}
          setPageCloser={setOpenExportPage}
          taskType="Export"
          exportData={businessProcessData.map((item) => item)}
          confluenceConfig={location.state?.confluenceConfig}
        />
      )}
    </Box>
  );
};

const Section = ({ title, children, align = "left" }) => (
  <Box
    sx={{
      mt: 4,
    }}
  >
    <Typography
      sx={{
        mb: 1,
        fontWeight: "bold",
        fontSize: "18px",
        textAlign: align,
      }}
    >
      {title}
    </Typography>
    <Box>{children}</Box>
  </Box>
);

const OverlayActions = ({ onDownload, onZoomIn, onZoomOut }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: 30,
      right: 8,
      display: "flex",
      alignItems: "center",
      gap: 1,
      bgcolor: "rgba(128, 128, 128, 0.8)",
      borderRadius: 1,
      p: 0.5,
    }}
  >
    <Tooltip title="Download">
      <IconButton onClick={onDownload} sx={{ color: "white" }} size="small">
        <FileDownloadOutlinedIcon />
      </IconButton>
    </Tooltip>
    <Tooltip title="Zoom In">
      <IconButton onClick={onZoomIn} sx={{ color: "white" }} size="small">
        <ZoomInIcon />
      </IconButton>
    </Tooltip>
    <Tooltip title="Zoom Out">
      <IconButton onClick={onZoomOut} sx={{ color: "white" }} size="small">
        <ZoomOutIcon />
      </IconButton>
    </Tooltip>

    {/* Sticky Footer */}

    <Box
      component="footer"
      sx={{
        width: "100%",
        maxWidth: "601px",
        bgcolor: "#f6f3f2",
        textAlign: "center",
        px: 2,
        py: 1,
        position: "fixed",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)", // ⬅ center align
        zIndex: 99,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Use QMentisAI™ with human supervision, as with all generative AI tools.
      </Typography>
    </Box>
  </Box>
);

export default MultiBusinessProcessGuide;
